import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Paper,
    Avatar,
    Button,
    Divider,
    LinearProgress,
    TextField,
    Alert,
    Tab,
    Tabs,
    useMediaQuery,
    useTheme,
    Stack
} from '@mui/material';
import {
    Recycling,
    CheckCircle,
    Cancel,
    LocalDrink,
    Build,
    ArrowBack,
    Nature,
    Newspaper,
    WineBar,
    Devices,
    TrendingUp,
    Calculate,
    LocationOn,
    Phone,
    Schedule,
    EmojiEvents,
    AttachMoney,
    Group,
    Factory
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: { xs: 2, sm: 3 } }}>{children}</Box>}
        </div>
    );
}

const RecyclableWastePage = () => {
    const [monthlyRecycling, setMonthlyRecycling] = useState(30);
    const [tabValue, setTabValue] = useState(0);
    const [expandedAccordion, setExpandedAccordion] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const recyclableItems = [
        {
            category: "Paper & Cardboard",
            icon: <Newspaper sx={{ fontSize: 40, color: '#2196f3' }} />,
            items: ["Newspapers", "Magazines", "Office paper", "Cardboard boxes", "Paper bags", "Books", "Envelopes", "Notebooks"],
            color: "#2196f3",
            recyclingRate: "85%",
            energySaved: "60%",
            marketValue: "৳8-12/kg",
            co2Reduction: "3.3 tons/ton"
        },
        {
            category: "Plastic Containers",
            icon: <LocalDrink sx={{ fontSize: 40, color: '#4caf50' }} />,
            items: ["Water bottles", "Food containers", "Detergent bottles", "Yogurt cups", "Plastic bags", "Bottle caps", "Shampoo bottles"],
            color: "#4caf50",
            recyclingRate: "25%",
            energySaved: "70%",
            marketValue: "৳15-25/kg",
            co2Reduction: "2.0 tons/ton"
        },
        {
            category: "Glass Items",
            icon: <WineBar sx={{ fontSize: 40, color: '#ff9800' }} />,
            items: ["Glass bottles", "Jars", "Wine bottles", "Beer bottles", "Food containers", "Perfume bottles", "Medicine bottles"],
            color: "#ff9800",
            recyclingRate: "70%",
            energySaved: "30%",
            marketValue: "৳5-8/kg",
            co2Reduction: "0.5 tons/ton"
        },
        {
            category: "Metal Materials",
            icon: <Build sx={{ fontSize: 40, color: '#9c27b0' }} />,
            items: ["Aluminum cans", "Steel cans", "Foil", "Bottle caps", "Small metal items", "Wire hangers", "Copper items"],
            color: "#9c27b0",
            recyclingRate: "90%",
            energySaved: "95%",
            marketValue: "৳40-80/kg",
            co2Reduction: "9.0 tons/ton"
        },
        {
            category: "Electronics",
            icon: <Devices sx={{ fontSize: 40, color: '#f44336' }} />,
            items: ["Old phones", "Computers", "Batteries", "Cables", "Small appliances", "Tablets", "Chargers"],
            color: "#f44336",
            recyclingRate: "20%",
            energySaved: "80%",
            marketValue: "৳50-200/kg",
            co2Reduction: "1.5 tons/ton"
        }
    ];

    const recyclingTips = [
        {
            title: "Clean Before Recycling",
            description: "Rinse containers to remove food residue and contaminants",
            icon: "🧽",
            impact: "90% better processing",
            difficulty: "Easy",
            timeRequired: "2 minutes"
        },
        {
            title: "Remove Labels",
            description: "Take off adhesive labels when possible for better processing",
            icon: "🏷️",
            impact: "70% quality improvement",
            difficulty: "Medium",
            timeRequired: "5 minutes"
        },
        {
            title: "Separate Materials",
            description: "Don't mix different types of recyclables in the same container",
            icon: "📦",
            impact: "85% efficiency boost",
            difficulty: "Easy",
            timeRequired: "1 minute"
        },
        {
            title: "Check Local Guidelines",
            description: "Follow your local recycling center's specific requirements",
            icon: "📋",
            impact: "100% compliance",
            difficulty: "Easy",
            timeRequired: "10 minutes"
        },
        {
            title: "Flatten Cardboard",
            description: "Save space and improve transport efficiency",
            icon: "📏",
            impact: "300% space saving",
            difficulty: "Easy",
            timeRequired: "30 seconds"
        },
        {
            title: "Sort by Color",
            description: "Separate glass and plastics by color for better recycling",
            icon: "🎨",
            impact: "60% quality boost",
            difficulty: "Medium",
            timeRequired: "3 minutes"
        }
    ];

    const bangladeshRecyclingStats = {
        currentRate: "15%",
        potential: "60%",
        jobs: "150,000+",
        revenue: "৳5,000 crore",
        co2Saved: "2.5M tons",
        landSaved: "500 acres"
    };

    const recyclingCenters = [
        {
            city: "Dhaka",
            centers: ["WASA Recycling Hub", "Tejgaon Industrial Area", "Savar EPZ Center"],
            contact: "+880-2-8391215",
            materials: ["Paper", "Plastic", "Metal", "Glass"],
            timings: "Daily 8 AM - 6 PM"
        },
        {
            city: "Chittagong",
            centers: ["Port Recycling Facility", "Kalurghat Center", "EPB Industrial Zone"],
            contact: "+880-31-2853333",
            materials: ["Metal", "Electronics", "Plastic"],
            timings: "Daily 9 AM - 5 PM"
        },
        {
            city: "Sylhet",
            centers: ["Sylhet Recycling Co-op", "Industrial Area Center"],
            contact: "+880-821-714491",
            materials: ["Paper", "Glass", "Plastic"],
            timings: "Mon-Sat 8 AM - 4 PM"
        }
    ];

    const recyclingProcess = [
        {
            step: "Collection",
            description: "Recyclables are collected from homes, businesses, and drop-off centers",
            participants: "Citizens, waste collectors, municipalities",
            timeframe: "Daily/Weekly"
        },
        {
            step: "Sorting",
            description: "Materials are separated by type using manual and automated systems",
            participants: "Facility workers, machines",
            timeframe: "1-2 days"
        },
        {
            step: "Cleaning",
            description: "Items are washed and processed to remove contaminants and labels",
            participants: "Processing plants",
            timeframe: "2-3 days"
        },
        {
            step: "Processing",
            description: "Materials are broken down, melted, or pulped into raw materials",
            participants: "Recycling facilities",
            timeframe: "3-7 days"
        },
        {
            step: "Manufacturing",
            description: "New products are made from recycled materials",
            participants: "Manufacturing companies",
            timeframe: "1-4 weeks"
        },
        {
            step: "Distribution",
            description: "Recycled products reach consumers, completing the cycle",
            participants: "Retailers, consumers",
            timeframe: "2-6 weeks"
        }
    ];

    const environmentalBenefits = [
        {
            benefit: "Saves natural resources",
            impact: "Reduces need for raw materials",
            statistic: "1 ton recycled paper = 17 trees saved",
            icon: "🌲"
        },
        {
            benefit: "Reduces landfill waste",
            impact: "Diverts 30-50% of waste from landfills",
            statistic: "50% reduction in municipal waste",
            icon: "🗑️"
        },
        {
            benefit: "Saves energy",
            impact: "Uses 60% less energy than producing new materials",
            statistic: "1 aluminum can = 3 hours of TV power",
            icon: "⚡"
        },
        {
            benefit: "Reduces pollution",
            impact: "Decreases air and water pollution significantly",
            statistic: "74% less air pollution from recycling",
            icon: "🌬️"
        },
        {
            benefit: "Creates jobs",
            impact: "Supports recycling industry employment",
            statistic: "10x more jobs than landfilling",
            icon: "👷"
        },
        {
            benefit: "Fights climate change",
            impact: "Reduces greenhouse gas emissions",
            statistic: "2.5M tons CO2 saved annually",
            icon: "🌍"
        }
    ];

    const dosDonts = {
        dos: [
            "Clean containers before recycling",
            "Remove caps and lids from bottles",
            "Flatten cardboard boxes to save space",
            "Keep recyclables dry and clean",
            "Follow the recycling symbols on packages",
            "Separate different material types",
            "Check local recycling guidelines",
            "Use designated recycling bins"
        ],
        donts: [
            "Put dirty or greasy items in recycling",
            "Mix different types of materials",
            "Include plastic bags with other recyclables",
            "Put broken glass in regular recycling",
            "Ignore local recycling guidelines",
            "Include non-recyclable plastics",
            "Put hazardous materials in recycling",
            "Contaminate clean materials"
        ]
    };

    const calculateEconomicImpact = () => {
        const avgValue = 15; // BDT per kg
        const annualValue = monthlyRecycling * 12 * avgValue;
        const environmentalValue = annualValue * 2; // Environmental benefit multiplier
        const communityJobs = Math.floor(monthlyRecycling * 12 / 1000); // Jobs created per ton
        return { annualValue, environmentalValue, communityJobs };
    };

    const { annualValue, environmentalValue, communityJobs } = calculateEconomicImpact();

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpandedAccordion(isExpanded ? panel : false);
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
            {/* Hero Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #2196f3 0%, #4caf50 100%)',
                    color: 'white',
                    py: { xs: 4, sm: 6, md: 8 },
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: 'radial-gradient(circle at 30% 70%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                        zIndex: 1
                    }
                }}
            >
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
                    <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} alignItems="center">
                        <Grid item xs={12} md={8}>
                            <Button
                                component={Link}
                                to="/"
                                startIcon={<ArrowBack />}
                                sx={{
                                    color: 'white',
                                    mb: { xs: 2, sm: 3 },
                                    fontSize: { xs: '0.875rem', sm: '1rem' }
                                }}
                            >
                                Back to Home
                            </Button>

                            <Alert
                                severity="success"
                                sx={{
                                    mb: { xs: 2, sm: 3 },
                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                    '& .MuiAlert-icon': { color: '#4caf50' },
                                    fontSize: { xs: '0.875rem', sm: '1rem' }
                                }}
                            >
                                <Typography fontWeight={600} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                    Every ton of recycling creates 10x more jobs than landfilling!
                                </Typography>
                            </Alert>

                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                alignItems={{ xs: 'center', sm: 'flex-start' }}
                                spacing={{ xs: 2, sm: 3 }}
                                sx={{ mb: { xs: 2, sm: 3 }, textAlign: { xs: 'center', sm: 'left' } }}
                            >
                                <Avatar
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        width: { xs: 60, sm: 70, md: 80 },
                                        height: { xs: 60, sm: 70, md: 80 }
                                    }}
                                >
                                    <Recycling sx={{ fontSize: { xs: 35, sm: 45, md: 50 }, color: 'white' }} />
                                </Avatar>
                                <Box>
                                    <Typography
                                        variant="h2"
                                        fontWeight={700}
                                        gutterBottom
                                        sx={{
                                            fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
                                            lineHeight: { xs: 1.2, sm: 1.3 }
                                        }}
                                    >
                                        ♻️ Recyclable Waste
                                    </Typography>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            opacity: 0.9,
                                            fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                                            lineHeight: { xs: 1.3, sm: 1.4 }
                                        }}
                                    >
                                        Transform waste into valuable resources through proper recycling
                                    </Typography>
                                </Box>
                            </Stack>

                            <Typography
                                variant="body1"
                                sx={{
                                    opacity: 0.8,
                                    maxWidth: { xs: '100%', md: 600 },
                                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                                    lineHeight: { xs: 1.5, sm: 1.6 },
                                    textAlign: { xs: 'center', sm: 'left' }
                                }}
                            >
                                Recycling is one of the most effective ways to reduce environmental impact.
                                Learn how to properly sort and prepare recyclable materials to give them a new life.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box
                                sx={{
                                    display: { xs: 'none', md: 'flex' },
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: 300,
                                    position: 'relative'
                                }}
                            >
                                <Recycling sx={{ fontSize: 200, color: 'rgba(255,255,255,0.3)' }} />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4, md: 6 } }}>
                {/* Bangladesh Recycling Statistics */}
                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 2, sm: 3, md: 4 },
                        mb: { xs: 4, sm: 6, md: 8 },
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c8 100%)',
                        border: '2px solid #4caf50'
                    }}
                >
                    <Typography
                        variant="h4"
                        fontWeight={700}
                        textAlign="center"
                        gutterBottom
                        sx={{
                            mb: { xs: 3, sm: 4 },
                            color: '#2e7d32',
                            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
                        }}
                    >
                        🇧🇩 Bangladesh Recycling Impact
                    </Typography>
                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                        {Object.entries(bangladeshRecyclingStats).map(([key, value], index) => (
                            <Grid item xs={6} sm={4} md={2} key={index}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography
                                        variant="h4"
                                        fontWeight={800}
                                        color="#4caf50"
                                        gutterBottom
                                        sx={{
                                            fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2.125rem' }
                                        }}
                                    >
                                        {value}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            textTransform: 'capitalize',
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                        }}
                                    >
                                        {key.replace(/([A-Z])/g, ' $1').trim()}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>

                {/* Economic Impact Calculator */}
                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 2, sm: 3, md: 4 },
                        mb: { xs: 4, sm: 6, md: 8 },
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                        border: '2px solid #2196f3'
                    }}
                >
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ xs: 'center', sm: 'flex-start' }}
                        spacing={2}
                        sx={{ mb: { xs: 2, sm: 3 }, textAlign: { xs: 'center', sm: 'left' } }}
                    >
                        <Calculate sx={{ color: '#2196f3', fontSize: { xs: 35, sm: 40 } }} />
                        <Typography
                            variant="h4"
                            fontWeight={700}
                            color="#1565c0"
                            sx={{
                                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
                            }}
                        >
                            💼 Personal Recycling Impact Calculator
                        </Typography>
                    </Stack>

                    <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="Monthly Recycling (kg)"
                                type="number"
                                value={monthlyRecycling}
                                onChange={(e) => setMonthlyRecycling(Number(e.target.value))}
                                fullWidth
                                sx={{ mb: { xs: 2, md: 0 } }}
                                size={isMobile ? "small" : "medium"}
                            />
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ textAlign: 'center' }}>
                                <Grid item xs={12} sm={4}>
                                    <Typography
                                        variant="h4"
                                        fontWeight={800}
                                        color="#2196f3"
                                        sx={{
                                            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
                                        }}
                                    >
                                        ৳{annualValue}
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontSize: { xs: '1rem', sm: '1.25rem' }
                                        }}
                                    >
                                        Economic Value
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Typography
                                        variant="h4"
                                        fontWeight={800}
                                        color="#4caf50"
                                        sx={{
                                            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
                                        }}
                                    >
                                        ৳{environmentalValue}
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontSize: { xs: '1rem', sm: '1.25rem' }
                                        }}
                                    >
                                        Environmental Benefit
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Typography
                                        variant="h4"
                                        fontWeight={800}
                                        color="#ff9800"
                                        sx={{
                                            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
                                        }}
                                    >
                                        {communityJobs}
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontSize: { xs: '1rem', sm: '1.25rem' }
                                        }}
                                    >
                                        Jobs Supported
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Enhanced Recyclable Materials with Tabs */}
                <Typography
                    variant="h3"
                    fontWeight={700}
                    textAlign="center"
                    gutterBottom
                    sx={{
                        mb: { xs: 4, sm: 6 },
                        fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
                    }}
                >
                    Types of Recyclable Materials
                </Typography>

                <Paper sx={{ mb: { xs: 4, sm: 6, md: 8 } }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        centered={!isMobile}
                        variant={isMobile ? "scrollable" : "standard"}
                        scrollButtons={isMobile ? "auto" : false}
                        sx={{
                            '& .MuiTab-root': {
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                minWidth: { xs: 80, sm: 120 }
                            }
                        }}
                    >
                        <Tab label="Overview" />
                        <Tab label="Market Values" />
                        <Tab label="Environmental Impact" />
                    </Tabs>

                    <TabPanel value={tabValue} index={0}>
                        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ p: { xs: 2, sm: 3 } }}>
                            {recyclableItems.map((item, index) => (
                                <Grid item xs={12} sm={6} lg={4} key={index}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            transition: 'transform 0.3s ease',
                                            '&:hover': {
                                                transform: isMobile ? 'none' : 'translateY(-8px)'
                                            },
                                            border: `2px solid ${item.color}20`,
                                            borderRadius: 4
                                        }}
                                    >
                                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={2}
                                                sx={{ mb: 2 }}
                                            >
                                                {React.cloneElement(item.icon, {
                                                    sx: { fontSize: { xs: 30, sm: 40 }, color: item.color }
                                                })}
                                                <Typography
                                                    variant="h6"
                                                    fontWeight={600}
                                                    sx={{
                                                        fontSize: { xs: '1rem', sm: '1.25rem' }
                                                    }}
                                                >
                                                    {item.category}
                                                </Typography>
                                            </Stack>

                                            <Box sx={{ mb: 2 }}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                                >
                                                    Current Recycling Rate:
                                                </Typography>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={parseInt(item.recyclingRate)}
                                                    sx={{
                                                        height: { xs: 6, sm: 8 },
                                                        borderRadius: 4,
                                                        backgroundColor: `${item.color}20`,
                                                        '& .MuiLinearProgress-bar': {
                                                            backgroundColor: item.color
                                                        }
                                                    }}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                                >
                                                    {item.recyclingRate} recycled in Bangladesh
                                                </Typography>
                                            </Box>

                                            <List dense>
                                                {item.items.slice(0, isMobile ? 3 : 4).map((subItem, idx) => (
                                                    <ListItem key={idx} sx={{ py: 0.5, px: 0 }}>
                                                        <ListItemIcon sx={{ minWidth: { xs: 25, sm: 30 } }}>
                                                            <CheckCircle sx={{ fontSize: { xs: 14, sm: 16 }, color: item.color }} />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={subItem}
                                                            sx={{
                                                                '& .MuiListItemText-primary': {
                                                                    fontSize: { xs: '0.875rem', sm: '1rem' }
                                                                }
                                                            }}
                                                        />
                                                    </ListItem>
                                                ))}
                                                {item.items.length > (isMobile ? 3 : 4) && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                                    >
                                                        +{item.items.length - (isMobile ? 3 : 4)} more items
                                                    </Typography>
                                                )}
                                            </List>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ p: { xs: 2, sm: 3 } }}>
                            {recyclableItems.map((item, index) => (
                                <Grid item xs={12} sm={6} key={index}>
                                    <Card elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderLeft: `4px solid ${item.color}` }}>
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                                            justifyContent="space-between"
                                            spacing={2}
                                            sx={{ mb: 2 }}
                                        >
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                {React.cloneElement(item.icon, {
                                                    sx: { fontSize: { xs: 30, sm: 40 } }
                                                })}
                                                <Typography
                                                    variant="h6"
                                                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                                                >
                                                    {item.category}
                                                </Typography>
                                            </Stack>
                                            <Chip
                                                label={item.marketValue}
                                                color="primary"
                                                icon={<AttachMoney />}
                                                size={isMobile ? "small" : "medium"}
                                            />
                                        </Stack>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                mb: 1,
                                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                            }}
                                        >
                                            Energy Saved: <strong>{item.energySaved}</strong> compared to new production
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                            }}
                                        >
                                            CO₂ Reduction: <strong>{item.co2Reduction}</strong> per ton recycled
                                        </Typography>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </TabPanel>

                    <TabPanel value={tabValue} index={2}>
                        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ p: { xs: 2, sm: 3 } }}>
                            {environmentalBenefits.map((benefit, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Card sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center', height: '100%', borderRadius: 4 }}>
                                        <Typography
                                            variant="h2"
                                            sx={{
                                                mb: 2,
                                                fontSize: { xs: '2rem', sm: '3rem' }
                                            }}
                                        >
                                            {benefit.icon}
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            fontWeight={600}
                                            gutterBottom
                                            sx={{
                                                fontSize: { xs: '1rem', sm: '1.25rem' }
                                            }}
                                        >
                                            {benefit.benefit}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                mb: 2,
                                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                            }}
                                        >
                                            {benefit.impact}
                                        </Typography>
                                        <Chip
                                            label={benefit.statistic}
                                            color="success"
                                            variant="outlined"
                                            size="small"
                                        />
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </TabPanel>
                </Paper>

                {/* Enhanced Recycling Tips with Accordion */}
                <Typography
                    variant="h3"
                    fontWeight={700}
                    textAlign="center"
                    gutterBottom
                    sx={{
                        mb: { xs: 4, sm: 6 },
                        fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
                    }}
                >
                    🎯 Advanced Recycling Best Practices
                </Typography>

                <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ mb: { xs: 4, sm: 6, md: 8 } }}>
                    {recyclingTips.map((tip, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Paper
                                elevation={3}
                                sx={{
                                    p: { xs: 2, sm: 3 },
                                    borderRadius: 4,
                                    height: '100%',
                                    transition: 'all 0.3s ease',
                                    border: '2px solid transparent',
                                    '&:hover': {
                                        transform: isMobile ? 'none' : 'translateY(-4px)',
                                        borderColor: '#2196f3',
                                        boxShadow: '0 8px 25px rgba(33, 150, 243, 0.15)'
                                    }
                                }}
                            >
                                <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3 } }}>
                                    <Typography
                                        variant="h2"
                                        sx={{
                                            mb: 2,
                                            fontSize: { xs: '2rem', sm: '3rem' }
                                        }}
                                    >
                                        {tip.icon}
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        fontWeight={600}
                                        gutterBottom
                                        sx={{
                                            fontSize: { xs: '1rem', sm: '1.25rem' }
                                        }}
                                    >
                                        {tip.title}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            mb: 2,
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                        }}
                                    >
                                        {tip.description}
                                    </Typography>
                                </Box>

                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={1}
                                    sx={{ mb: 2, flexWrap: 'wrap' }}
                                >
                                    <Chip
                                        label={tip.impact}
                                        color="success"
                                        size="small"
                                        icon={<TrendingUp />}
                                    />
                                    <Chip
                                        label={tip.difficulty}
                                        color={tip.difficulty === 'Easy' ? 'success' : 'warning'}
                                        size="small"
                                    />
                                    <Chip
                                        label={tip.timeRequired}
                                        color="info"
                                        size="small"
                                    />
                                </Stack>

                                <Button
                                    fullWidth
                                    variant="outlined"
                                    sx={{ mt: 2 }}
                                    size={isMobile ? "small" : "medium"}
                                >
                                    Learn More
                                </Button>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* Local Recycling Centers */}
                <Typography
                    variant="h3"
                    fontWeight={700}
                    textAlign="center"
                    gutterBottom
                    sx={{
                        mb: { xs: 4, sm: 6 },
                        fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
                    }}
                >
                    🏭 Local Recycling Centers
                </Typography>

                <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ mb: { xs: 4, sm: 6, md: 8 } }}>
                    {recyclingCenters.map((center, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <Card
                                elevation={3}
                                sx={{
                                    p: { xs: 2, sm: 3 },
                                    borderRadius: 4,
                                    height: '100%',
                                    border: '2px solid #4caf50'
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <LocationOn sx={{ color: '#4caf50' }} />
                                    <Typography
                                        variant="h5"
                                        fontWeight={600}
                                        color="#2e7d32"
                                        sx={{
                                            fontSize: { xs: '1.25rem', sm: '1.5rem' }
                                        }}
                                    >
                                        {center.city}
                                    </Typography>
                                </Stack>

                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                    sx={{
                                        mb: 1,
                                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                    }}
                                >
                                    Recycling Centers:
                                </Typography>
                                <List dense>
                                    {center.centers.map((loc, idx) => (
                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: 20 }}>
                                                <Factory sx={{ fontSize: 16, color: '#757575' }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={loc}
                                                sx={{
                                                    '& .MuiListItemText-primary': {
                                                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                                    }
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>

                                <Divider sx={{ my: 2 }} />

                                <Box sx={{ mb: 2 }}>
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                        sx={{
                                            mb: 1,
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                        }}
                                    >
                                        Accepted Materials:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {center.materials.map((material, idx) => (
                                            <Chip
                                                key={idx}
                                                label={material}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        ))}
                                    </Box>
                                </Box>

                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                    <Phone sx={{ color: '#757575', fontSize: 20 }} />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                        }}
                                    >
                                        {center.contact}
                                    </Typography>
                                </Stack>

                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Schedule sx={{ color: '#757575', fontSize: 20 }} />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                        }}
                                    >
                                        {center.timings}
                                    </Typography>
                                </Stack>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Recycling Process with Detailed Information */}
                <Typography
                    variant="h3"
                    fontWeight={700}
                    textAlign="center"
                    gutterBottom
                    sx={{
                        mb: { xs: 4, sm: 6 },
                        fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
                    }}
                >
                    🔄 Complete Recycling Process
                </Typography>

                <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 4, sm: 6, md: 8 } }}>
                    {recyclingProcess.map((step, index) => (
                        <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
                            <Card sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center', height: '100%', borderRadius: 4 }}>
                                <Avatar
                                    sx={{
                                        bgcolor: '#2196f3',
                                        width: { xs: 40, sm: 50 },
                                        height: { xs: 40, sm: 50 },
                                        mx: 'auto',
                                        mb: 2,
                                        fontSize: { xs: '1rem', sm: '1.2rem' }
                                    }}
                                >
                                    {index + 1}
                                </Avatar>
                                <Typography
                                    variant="h6"
                                    fontWeight={600}
                                    gutterBottom
                                    sx={{
                                        fontSize: { xs: '1rem', sm: '1.25rem' }
                                    }}
                                >
                                    {step.step}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        mb: 2,
                                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                    }}
                                >
                                    {step.description}
                                </Typography>
                                <Chip
                                    label={step.timeframe}
                                    size="small"
                                    color="info"
                                    sx={{ mb: 1 }}
                                />
                                <Typography
                                    variant="caption"
                                    display="block"
                                    color="text.secondary"
                                    sx={{
                                        fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                    }}
                                >
                                    {step.participants}
                                </Typography>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Enhanced Do's and Don'ts */}
                <Typography
                    variant="h3"
                    fontWeight={700}
                    textAlign="center"
                    gutterBottom
                    sx={{
                        mb: { xs: 4, sm: 6 },
                        fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
                    }}
                >
                    📋 Recycling Guidelines
                </Typography>

                <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ mb: { xs: 4, sm: 6, md: 8 } }}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%', border: '2px solid #4caf50', borderRadius: 4 }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                                <Typography
                                    variant="h5"
                                    fontWeight={700}
                                    sx={{
                                        color: '#4caf50',
                                        mb: { xs: 2, sm: 3 },
                                        fontSize: { xs: '1.25rem', sm: '1.5rem' }
                                    }}
                                >
                                    ✅ Essential Do's
                                </Typography>
                                <List>
                                    {dosDonts.dos.map((item, index) => (
                                        <ListItem key={index} sx={{ px: 0 }}>
                                            <ListItemIcon>
                                                <CheckCircle sx={{ color: '#4caf50' }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={item}
                                                sx={{
                                                    '& .MuiListItemText-primary': {
                                                        fontWeight: 500,
                                                        fontSize: { xs: '0.875rem', sm: '1rem' }
                                                    }
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%', border: '2px solid #f44336', borderRadius: 4 }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                                <Typography
                                    variant="h5"
                                    fontWeight={700}
                                    sx={{
                                        color: '#f44336',
                                        mb: { xs: 2, sm: 3 },
                                        fontSize: { xs: '1.25rem', sm: '1.5rem' }
                                    }}
                                >
                                    ❌ Critical Don'ts
                                </Typography>
                                <List>
                                    {dosDonts.donts.map((item, index) => (
                                        <ListItem key={index} sx={{ px: 0 }}>
                                            <ListItemIcon>
                                                <Cancel sx={{ color: '#f44336' }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={item}
                                                sx={{
                                                    '& .MuiListItemText-primary': {
                                                        fontWeight: 500,
                                                        fontSize: { xs: '0.875rem', sm: '1rem' }
                                                    }
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Enhanced Call to Action */}
                <Paper
                    elevation={6}
                    sx={{
                        p: { xs: 3, sm: 4, md: 6 },
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)',
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        variant="h3"
                        fontWeight={700}
                        gutterBottom
                        sx={{
                            fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
                        }}
                    >
                        📊 Your Recycling Impact
                    </Typography>
                    <Divider sx={{ my: 3, maxWidth: 200, mx: 'auto', backgroundColor: '#2196f3' }} />

                    <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ mb: { xs: 3, sm: 4 } }}>
                        <Grid item xs={12} md={4}>
                            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
                                <EmojiEvents sx={{ fontSize: { xs: 36, sm: 48 }, color: '#ff9800', mb: 2 }} />
                                <Typography
                                    variant="h5"
                                    fontWeight={600}
                                    gutterBottom
                                    sx={{
                                        fontSize: { xs: '1.25rem', sm: '1.5rem' }
                                    }}
                                >
                                    Economic Impact
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                    }}
                                >
                                    Your recycling generates ৳{annualValue} in economic value annually
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
                                <Nature sx={{ fontSize: { xs: 36, sm: 48 }, color: '#4caf50', mb: 2 }} />
                                <Typography
                                    variant="h5"
                                    fontWeight={600}
                                    gutterBottom
                                    sx={{
                                        fontSize: { xs: '1.25rem', sm: '1.5rem' }
                                    }}
                                >
                                    Environmental Benefit
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                    }}
                                >
                                    Creating ৳{environmentalValue} worth of environmental benefits
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
                                <Group sx={{ fontSize: { xs: 36, sm: 48 }, color: '#2196f3', mb: 2 }} />
                                <Typography
                                    variant="h5"
                                    fontWeight={600}
                                    gutterBottom
                                    sx={{
                                        fontSize: { xs: '1.25rem', sm: '1.5rem' }
                                    }}
                                >
                                    Community Impact
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                    }}
                                >
                                    Supporting {communityJobs} jobs in the recycling industry
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        justifyContent="center"
                        sx={{ mt: { xs: 4, sm: 6 } }}
                    >
                        <Button
                            component={Link}
                            to="/classify"
                            variant="contained"
                            size={isMobile ? "medium" : "large"}
                            sx={{
                                px: { xs: 3, sm: 4 },
                                py: { xs: 1.5, sm: 2 },
                                background: 'linear-gradient(135deg, #2196f3 0%, #4caf50 100%)',
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #1976d2 0%, #388e3c 100%)'
                                }
                            }}
                        >
                            Start Recycling Today
                        </Button>
                        <Button
                            component={Link}
                            to="/tips"
                            variant="outlined"
                            size={isMobile ? "medium" : "large"}
                            sx={{
                                px: { xs: 3, sm: 4 },
                                py: { xs: 1.5, sm: 2 },
                                fontSize: { xs: '0.875rem', sm: '1rem' }
                            }}
                        >
                            Learn More Tips
                        </Button>
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
};

export default RecyclableWastePage;
